import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { SystemJobData, SystemJobApiResponse } from './systemJob.types';

// Standardized GET function for system jobs list
export async function fetchSystemJobs({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  clientId,
  jobName,
  status,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  clientId?: number;
  jobName?: string;
  status?: string;
  searched?: boolean;
}): Promise<SystemJobData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobName) params.jobName = jobName;
  if (status) params.status = status;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.SYSTEM_JOB.LIST);
  const response: SystemJobApiResponse[] = await api.get<SystemJobApiResponse[]>(endpoint, params);
  
  return response.map((job: SystemJobApiResponse) => ({
    id: job.id || 0,
    jobName: job.jobName || '',
    lastStartTime: job.lastStartTime?.toString() || '',
    lastEndTime: job.lastEndTime?.toString() || '',
    nextRunTime: job.nextRunTime?.toString() || '',
    interval: job.interval || 0,
  }));
}

// GET system jobs count
export async function fetchSystemJobsCount({
  clientCode,
  clientId,
  jobName,
  status,
  searched = true,
}: {
  clientCode: string;
  clientId?: number;
  jobName?: string;
  status?: string;
  searched?: boolean;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobName) params.jobName = jobName;
  if (status) params.status = status;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.SYSTEM_JOB.COUNT);
  try {
    const response = await api.get(endpoint, params);
    
    // Handle different response formats
    if (typeof response === 'number') {
      return response;
    } else if (response && typeof response === 'object' && 'count' in response) {
      const countValue = (response as any).count;
      return typeof countValue === 'number' ? countValue : 0;
    } else {
      logger.warn('Unexpected count response format:', response);
      return 0;
    }
  } catch (error) {
    logger.error('Error fetching system jobs count:', error);
    return 0;
  }
}

// GET system job by ID
export async function fetchSystemJobById({
  clientCode,
  jobId,
}: {
  clientCode: string;
  jobId: number;
}): Promise<SystemJobData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.SYSTEM_JOB.LIST}/${jobId}`);
  const response: SystemJobApiResponse = await api.get<SystemJobApiResponse>(endpoint);
  
  return {
    id: response.id || 0,
    jobName: response.jobName || '',
    lastStartTime: response.lastStartTime?.toString() || '',
    lastEndTime: response.lastEndTime?.toString() || '',
    nextRunTime: response.nextRunTime?.toString() || '',
    interval: response.interval || 0,
  };
}

// GET system job detail
export async function fetchSystemJobDetail({
  clientCode,
  jobId,
}: {
  clientCode: string;
  jobId: number;
}): Promise<any> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.SYSTEM_JOB.LIST}/${jobId}`);
  return api.get(endpoint);
}

// CREATE system job
export async function createSystemJob({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<SystemJobData> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.SYSTEM_JOB.CREATE);
  const response: SystemJobApiResponse = await api.post<SystemJobApiResponse>(endpoint, payload);
  
  return {
    id: response.id || 0,
    jobName: response.jobName || '',
    lastStartTime: response.lastStartTime?.toString() || '',
    lastEndTime: response.lastEndTime?.toString() || '',
    nextRunTime: response.nextRunTime?.toString() || '',
    interval: response.interval || 0,
  };
}

// UPDATE system job
export async function updateSystemJob({
  clientCode,
  jobId,
  payload,
}: {
  clientCode: string;
  jobId: number;
  payload: any;
}): Promise<SystemJobData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.SYSTEM_JOB.UPDATE}/${jobId}`);
  const response: SystemJobApiResponse = await api.put<SystemJobApiResponse>(endpoint, payload);
  
  return {
    id: response.id || 0,
    jobName: response.jobName || '',
    lastStartTime: response.lastStartTime?.toString() || '',
    lastEndTime: response.lastEndTime?.toString() || '',
    nextRunTime: response.nextRunTime?.toString() || '',
    interval: response.interval || 0,
  };
}

// DELETE system job
export async function deleteSystemJob({
  clientCode,
  jobId,
}: {
  clientCode: string;
  jobId: number;
}): Promise<void> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.SYSTEM_JOB.DELETE}/${jobId}`);
  return api.delete(endpoint);
}
